import { isRunningInNativeApp } from './nativeBridge'

/**
 * Untiled basemap images are decoded in full by mapbox-gl the moment the
 * source is added — ImageSource.onAdd() fetches and keeps the decoded bitmap
 * (width × height × 4 bytes of RGBA) regardless of viewport, and visited
 * images additionally get a GPU texture copy. Inside the native app's
 * WKWebView those decodes stack and OOM-kill the WebContent process
 * ("Webview process terminated"), so oversized plans are skipped there:
 * per image over MAX_MOBILE_DECODED_BYTES, and collectively once admitted
 * plans would exceed CUMULATIVE_DECODED_BUDGET (admission is released on
 * unmount). File size is no proxy for decoded size — dimensions are read from
 * the image header (PNG IHDR / JPEG SOF) via a streamed fetch that is
 * cancelled after the first chunks; the image itself is never downloaded or
 * decoded. Unknown formats and network errors fail open.
 */

export interface IImageDimensions {
  width: number
  height: number
}

export interface IBasemapAdmission {
  result: Promise<boolean>
  release: () => void
}

const MAX_MOBILE_DECODED_BYTES = 100 * 1024 * 1024
const CUMULATIVE_DECODED_BUDGET = 150 * 1024 * 1024
const BYTES_PER_PIXEL = 4
const HEADER_READ_LIMIT = 128 * 1024

const PNG_SIGNATURE = [0x89, 0x50, 0x4e, 0x47, 0x0d, 0x0a, 0x1a, 0x0a]

export function isBasemapGuardActive(): boolean {
  return isRunningInNativeApp()
}

export function parseImageDimensions(
  bytes: Uint8Array,
): IImageDimensions | null {
  return parsePngDimensions(bytes) || parseJpegDimensions(bytes)
}

function parsePngDimensions(bytes: Uint8Array): IImageDimensions | null {
  if (bytes.length < 24) {
    return null
  }
  if (PNG_SIGNATURE.some((byte, i) => bytes[i] !== byte)) {
    return null
  }
  const width = readUint32(bytes, 16)
  const height = readUint32(bytes, 20)
  return width && height ? { width, height } : null
}

function parseJpegDimensions(bytes: Uint8Array): IImageDimensions | null {
  if (bytes.length < 4 || bytes[0] !== 0xff || bytes[1] !== 0xd8) {
    return null
  }
  let offset = 2
  while (offset + 9 <= bytes.length) {
    if (bytes[offset] !== 0xff) {
      return null
    }
    const marker = bytes[offset + 1]
    if (marker === 0xff) {
      offset++
      continue
    }
    if (marker === 0x01 || (marker >= 0xd0 && marker <= 0xd9)) {
      offset += 2
      continue
    }
    const isStartOfFrame =
      marker >= 0xc0 &&
      marker <= 0xcf &&
      marker !== 0xc4 &&
      marker !== 0xc8 &&
      marker !== 0xcc
    if (isStartOfFrame) {
      const height = (bytes[offset + 5] << 8) | bytes[offset + 6]
      const width = (bytes[offset + 7] << 8) | bytes[offset + 8]
      return width && height ? { width, height } : null
    }
    const segmentLength = (bytes[offset + 2] << 8) | bytes[offset + 3]
    if (segmentLength < 2) {
      return null
    }
    offset += 2 + segmentLength
  }
  return null
}

function readUint32(bytes: Uint8Array, offset: number): number {
  return (
    ((bytes[offset] << 24) |
      (bytes[offset + 1] << 16) |
      (bytes[offset + 2] << 8) |
      bytes[offset + 3]) >>>
    0
  )
}

async function fetchImageDimensions(
  url: string,
): Promise<IImageDimensions | null> {
  const response = await fetch(url)
  if (!response.ok || !response.body) {
    return null
  }
  const reader = response.body.getReader()
  try {
    let bytes = new Uint8Array(0)
    while (bytes.length < HEADER_READ_LIMIT) {
      const { done, value } = await reader.read()
      if (done) {
        return null
      }
      const merged = new Uint8Array(bytes.length + value.length)
      merged.set(bytes)
      merged.set(value, bytes.length)
      bytes = merged
      const dimensions = parseImageDimensions(bytes)
      if (dimensions) {
        return dimensions
      }
    }
    return null
  } finally {
    reader.cancel().catch(() => undefined)
  }
}

const dimensionsByUrl = new Map<string, Promise<IImageDimensions | null>>()

function getImageDimensions(url: string): Promise<IImageDimensions | null> {
  if (!dimensionsByUrl.has(url)) {
    dimensionsByUrl.set(
      url,
      fetchImageDimensions(url).catch(() => null),
    )
  }
  return dimensionsByUrl.get(url)
}

let admittedBytes = 0

export function requestBasemapAdmission(url: string): IBasemapAdmission {
  let grantedBytes = 0
  let released = false
  const result = (async () => {
    if (!isBasemapGuardActive()) {
      return true
    }
    const dimensions = await getImageDimensions(url)
    if (released) {
      return false
    }
    if (!dimensions) {
      return true
    }
    const bytes = dimensions.width * dimensions.height * BYTES_PER_PIXEL
    if (bytes > MAX_MOBILE_DECODED_BYTES) {
      return false
    }
    if (admittedBytes + bytes > CUMULATIVE_DECODED_BUDGET) {
      return false
    }
    admittedBytes += bytes
    grantedBytes = bytes
    return true
  })()
  return {
    result,
    release: () => {
      if (released) {
        return
      }
      released = true
      void result.then(() => {
        admittedBytes -= grantedBytes
        grantedBytes = 0
      })
    },
  }
}
